import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useScheduleContext } from '../../../../../context/ScheduleContext';
import { useLocaleContext } from '../../../../../context/LocaleContext';
import { useModalContext } from '../../../../../context/ModalContext';
import { hexToRgba } from '../../../../../helpers/hexToRgb';
import { unixFormat } from '../../../../../helpers/times';
import { episodeMembers, getEpisodeStatus } from '../../../../../helpers/schedule-helper';
import { CButton, CTags } from '../../../../UI';
import ScheduleEpisodeStatus from '../../../ScheduleComponents/ScheduleEpisodeStatus';
import ScheduleHeaderActions from '../../../ScheduleComponents/ScheduleHeaderActions';
import ScheduleUserCategory from '../../../ScheduleComponents/ScheduleUserCategory';
import ScheduleFullness from '../../../ScheduleComponents/ScheduleFullness';
import MembersSimpleView from '../../../../Members/MembersComponents/MembersSimpleView';
import MemberDetailsModal from '../../../../Members/MembersComponents/MemberDetailsModal';
import PartnersBaseView from '../../../../Partners/PartnersViews/PartnersBaseView';
import EpisodeRecordStatus from '../../../ScheduleComponents/EpisodeRecordStatus';
import { useApplicationContext } from '../../../../../context/ApplicationContext';
import { useEventContext } from '../../../../../context/EventContext';
import { useStreamContext } from '../../../../../context/StreamContext';
import { EPISODE_STATUSES, RECORD_STATUSES, TYPE_OFFLINE } from '../../../../../constants/common';
import storage from '../../../../../../../player/js/storage';
import Icons from '../../../../Icons';

import './style.scss';

const ScheduleTileCard = ({
    episode,
    onClick,
    currentUser,
    checkAuth,
    onRateEpisode,
    isShowWatchRecording,
}) => {
    const {page, hasManyHalls, isWidget, widgetSettings} = useScheduleContext();
    const { handleBookmark, bookmarkEpisodeIds } = useApplicationContext();
    const { event, watchKey } = useEventContext();
    const $locale = useLocaleContext();
    const { eventKey } = useEventContext();
    const {renderModal} = useModalContext();
    const { recordStatus } = useStreamContext();
    const navigate = useNavigate();
    const ratedStorageKey = `${eventKey}/episodes/rated`;
    const ratedEpisodes = storage.get(ratedStorageKey) || {};
    const [isRated, setIsRated] = useState(ratedEpisodes[episode.id]?.includes(currentUser?.id));
    const episodeWatchPage = currentUser?.participationType === TYPE_OFFLINE
        ? 'halls'
        : 'broadcast';

    const isOpenInPlayer = useMemo(() => {
        const episodeStatus = getEpisodeStatus(episode);

        return isShowWatchRecording
            && (recordStatus === RECORD_STATUSES.EPISODES_CUT
            || episodeStatus === EPISODE_STATUSES.record
            || episodeStatus === EPISODE_STATUSES.live);
    }, [episode, isShowWatchRecording]);

    const handleRateToggle = async (e) => {
        e.stopPropagation();

        const isAuth = await checkAuth(currentUser);
        if (isAuth) {
            const value = !isRated;
            setIsRated(isRated => !isRated);
            const ratedEpisodes = storage.get(ratedStorageKey) || {};
            const arr = ratedEpisodes[episode.id] || [];
            const ratedStorageValue = { ...ratedEpisodes, [episode.id]: value ? [...arr, currentUser?.id] : arr.filter(id => id !== currentUser?.id) };
            onRateEpisode(ratedStorageKey, episode.id, value, ratedStorageValue);
        }
    }

    return (
        <div
            className="schedule-card-tile"
            style={{
                borderTop: (!!episode.stream?.color && !episode.hideStreamColor) ? `2px solid ${episode.stream.color}` : 'none',
                backgroundColor: episode.paintInStreamColor && !!episode.stream.color ? hexToRgba(episode.stream.color, 10) : '',
            }}
            onClick={onClick}
        >
            <div className="schedule-card-tile-wrapper">
                {
                    (hasManyHalls && !episode.hideStreamDescription) &&
                    <div className="schedule-card-tile-hall">
                        <p>
                            {$locale.getProperty(episode.stream, 'description')}
                        </p>
                    </div>
                }

                <div className="schedule-card-tile-header">
                    <div className="schedule-card-tile-header__time">
                        {unixFormat(episode.timeStart, 'HH:mm')} — {unixFormat(episode.timeStop, 'HH:mm')}
                    </div>
                    <div className="schedule-card-tile-header__info">
                        <ScheduleEpisodeStatus
                            timeStart={episode.timeStart}
                            timeStop={episode.timeStop}
                            isVisible={isWidget ? !!widgetSettings?.showTileStatus : page?.settings?.showStatuses}
                        />
                        <EpisodeRecordStatus
                            episode={episode}
                            displayRecord={widgetSettings?.displayEpisodeRecord ?? false}
                        >
                            <ScheduleHeaderActions
                                episode={episode}
                                isFormatVisible={isWidget ? !!widgetSettings?.showTileParticipationType : (page?.settings?.showFormats ?? true)}
                                isCalendarVisible={
                                    isWidget ? widgetSettings?.showTileAddToCalendar : page?.settings?.showAddToCalendar ?? true
                                }
                                isBookmarkVisible={!isWidget && event?.moduleEpisode?.episodeFavorite}
                                handleBookmark={handleBookmark}
                                bookmarkEpisodeIds={bookmarkEpisodeIds}
                            />
                        </EpisodeRecordStatus>
                    </div>
                </div>
                {/* Временно скрыто */}
                {/* <div className="schedule-card-tile-row">
                    <ScheduleUserCategory />
                    <ScheduleFullness />
                </div> */}
                <h2 className="schedule-card-tile-title">
                    {$locale.getProperty(episode, 'name')}
                </h2>
                {
                    !!episode.tags?.length &&
                    <CTags tags={episode.tags} />
                }
                {
                        !!episode.partners?.length &&
                        <div style={{paddingTop: 10}}>
                            <PartnersBaseView
                                partners={episode.partners}
                                size="xs"
                            />
                        </div>
                    }
                {
                    !!episode.groups?.length &&
                    <div
                        className="schedule-card-tile-speakers"
                        style={
                            isWidget
                            ? {
                                '--app-border-radius-photo': widgetSettings?.tilePhotoRoundingSize,
                            }
                            : {}
                        }
                    >
                        <MembersSimpleView
                            episode={episode}
                            hasPhotos={isWidget ? widgetSettings?.showTileMembersPhoto : true}
                            hasNames={isWidget ? widgetSettings?.showTileMembersName : true}
                            onMemberClick={(member) => {
                                renderModal(
                                    MemberDetailsModal,
                                    {member, lockPageScroll: false, settings: isWidget ? widgetSettings : page?.settings},
                                    null,
                                    (isWidget ? widgetSettings?.showMemberPopup : true)
                                )
                            }}
                        />
                    </div>
                }
            </div>
            <div className="schedule-card-tile-footer">
                {onRateEpisode && (
                    <a onClick={handleRateToggle} className={`schedule-card-tile-like ${isRated ? '-active' : ''}`}>
                        <Icons name="like_message" active={isRated} />{episode.like}
                    </a>
                )}
                {isOpenInPlayer &&
                    <CButton
                        label={$locale.get('label.episode.watch')}
                        blank
                        icon="play"
                        className="schedule-card-tile-watch"
                        onClick={() => navigate(`/event/${watchKey}/${episodeWatchPage}?stream=${episode.streamId}&episode=${episode.id}`)}
                    />
                }
            </div>
        </div>
    )
};

export default ScheduleTileCard;
